/**
 * Protected Route Component
 * 
 * Protects routes by checking both authentication and page permissions.
 * Redirects to dashboard if user doesn't have access to the page.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/app/providers/PermissionsContext.jsx';
import { LoadingSpinner } from '@/shared/components';

function ProtectedRoute({ children }) {
  const location = useLocation();
  const { hasPageAccess, isInitialized } = usePermissions();

  let isAuthenticated = false;
  try {
    isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('ProtectedRoute: Error accessing localStorage', error);
    // Default to not authenticated if localStorage fails
    isAuthenticated = false;
  }

  try {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    // Wait for permissions to load
    if (!isInitialized) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: '16px',
          backgroundColor: 'var(--background)'
        }}>
          <LoadingSpinner size="large" />
          <div style={{
            fontSize: '16px',
            color: 'var(--text-muted)'
          }}>
            Loading...
          </div>
        </div>
      );
    }

    // Check if user has access to this page
    if (!hasPageAccess(location.pathname)) {
      // eslint-disable-next-line no-console
      console.warn(`ProtectedRoute: Access denied to ${location.pathname}`);
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('ProtectedRoute error:', error);
    // Fallback to rendering children to prevent complete lockout
    return children;
  }
}

export default ProtectedRoute;

